const RESEND_DEBOUNCE_MS = 400;
const RESEND_SUCCESS_TIMEOUT_MS = 4000;

const OTP_MSG_FALLBACKS = {
    otp_verify_modal_incorrect: {
        en: "The OTP code is incorrect, please try again.",
        vi: "Mã xác thực OTP không chính xác, vui lòng thử lại.",
    },
    otp_verify_modal_resend_success: {
        en: "OTP has been resent successfully.",
        vi: "Mã OTP đã được gửi lại thành công.",
    },
    otp_verify_call_lock: {
        en: "OTP limit reached. Please try again tomorrow.",
        vi: "Đã đạt giới hạn OTP trong ngày. Vui lòng thử lại vào ngày mai.",
    },
    otp_verify_blocked: {
        en: "Too many incorrect OTP attempts. Please try again tomorrow.",
        vi: "Bạn đã nhập sai mã OTP quá số lần cho phép. Vui lòng thử lại vào ngày mai.",
    },
    otp_verify_soft_blocked: {
        en: "Too many incorrect OTP attempts. Please request a new code to try again.",
        vi: "Bạn đã nhập sai mã OTP quá nhiều lần. Vui lòng bấm 'Gửi lại mã' để nhận mã mới và thử lại.",
    },
    otp_verify_expired: {
        en: "OTP code has expired.",
        vi: "Mã OTP đã hết hạn.",
    },
    otp_send_rate_limit_phone: {
        en: "You have reached the maximum of %d OTP sends today for this phone number. Please try again tomorrow.",
        vi: "Bạn đã gửi OTP tối đa %d lần trong ngày với số điện thoại này. Vui lòng thử lại vào ngày mai.",
    },
    otp_send_rate_limit_ip: {
        en: "You have reached the maximum of %d OTP sends today. Please try again tomorrow.",
        vi: "Bạn đã gửi OTP tối đa %d lần trong ngày. Vui lòng thử lại vào ngày mai.",
    },
    otp_send_resend_cooldown: {
        en: "Please wait before requesting another OTP code.",
        vi: "Vui lòng đợi trước khi gửi lại mã OTP.",
    },
    otp_verify_failed: {
        en: "OTP verification failed. Please try again.",
        vi: "Xác thực OTP thất bại. Vui lòng thử lại.",
    },
    otp_send_failed: {
        en: "Failed to send OTP",
        vi: "Gửi OTP thất bại",
    },
};

const OTP_VERIFY_ERROR_KEYS = {
    otp_incorrect: "otp_verify_modal_incorrect",
    otp_send_blocked: "otp_verify_call_lock",
    otp_verify_blocked: "otp_verify_blocked",
    otp_expired: "otp_verify_expired",
};

const OTP_SEND_ERROR_KEYS = {
    rate_limit_phone: "otp_send_rate_limit_phone",
    rate_limit_ip: "otp_send_rate_limit_ip",
    resend_cooldown: "otp_send_resend_cooldown",
};

export default class OtpVerifyFlow {
    constructor() {
        if (!$("#otp-verify-modal").length) return;
        this._sendOtpInFlight = null;
        this._resendDebounceTimer = null;
        this._successMessageTimer = null;
        this._otpLockedForToday = false;
        this._otpVerifyCodeBurned = false;
        this.currentMode = "booking";
        this.currentPassword = "";
        this._submitBtnDefaultText =
            $("#otp-submit-btn .context").text().trim() || "Submit";
        this.initInputs();
        this.initCooldown();
        this.initProcessingGuards();
    }

    initProcessingGuards() {
        $(document).on(
            "click.otpVerifyProcessing",
            "#otp-verify-modal-wrapper.is-processing .close-modal",
            (e) => {
                e.preventDefault();
                e.stopImmediatePropagation();
            }
        );
    }

    getLang() {
        return typeof HM_Variables !== "undefined" && HM_Variables.language === "en"
            ? "en"
            : "vi";
    }

    t(key, fallback) {
        const fromLang =
            typeof HM_Variables !== "undefined" && HM_Variables.frontend_lang?.[key];
        if (fromLang) {
            return fromLang;
        }

        const fb = fallback ?? OTP_MSG_FALLBACKS[key];
        if (typeof fb === "string") {
            return fb;
        }
        if (fb && typeof fb === "object") {
            return this.getLang() === "en" ? fb.en : fb.vi;
        }

        return "";
    }

    getDailySendLimit(source = {}) {
        const isIpLimit = source.error_code === "rate_limit_ip";
        const isRegister = this.isRegisterMode();
        const fromSource = Number(
            isIpLimit
                ? source.daily_limit_ip ?? source.data?.daily_limit_ip
                : source.daily_limit_phone ?? source.data?.daily_limit_phone
        );
        if (fromSource > 0) {
            return fromSource;
        }

        if (typeof HM_Variables !== "undefined") {
            const fromVars = Number(
                isIpLimit
                    ? isRegister
                        ? HM_Variables.register_otp_daily_send_limit_ip
                        : HM_Variables.otp_daily_send_limit_ip
                    : isRegister
                      ? HM_Variables.register_otp_daily_send_limit
                      : HM_Variables.otp_daily_send_limit
            );
            if (fromVars > 0) {
                return fromVars;
            }
        }

        return 5;
    }

    isRegisterMode() {
        return this.currentMode === "register";
    }

    getOtpActions() {
        if (this.isRegisterMode()) {
            return {
                status: "register_otp_status",
                send: "register_send_otp",
                verify: "register_verify_otp",
            };
        }
        return {
            status: "otp_status",
            send: "send_otp",
            verify: "verify_otp",
        };
    }

    buildSendPayload(identifier) {
        if (this.isRegisterMode()) {
            return {
                username: identifier,
                password: this.currentPassword || "",
            };
        }
        return { phone: identifier };
    }

    buildVerifyPayload(identifier, otp) {
        if (this.isRegisterMode()) {
            return {
                username: identifier,
                password: this.currentPassword || "",
                otp,
            };
        }
        return { phone: identifier, otp };
    }

    buildStatusPayload(identifier) {
        if (this.isRegisterMode()) {
            return { username: identifier };
        }
        return { phone: identifier };
    }

    formatWithDailyLimit(template, limit) {
        const safeLimit = Math.max(1, Number(limit) || 5);
        return String(template || "").replace(/%d/g, String(safeLimit));
    }

    getVerifyLoadingText() {
        return this.getLang() === "en" ? "Verifying..." : "Đang xác thực...";
    }

    getBookingLoadingText() {
        return this.getLang() === "en" ? "Submitting booking..." : "Đang đặt lịch...";
    }

    getOtpSendErrorMessage(res = {}) {
        const langKey = OTP_SEND_ERROR_KEYS[res.error_code];
        if (langKey) {
            const msg = this.t(langKey);
            if (msg) {
                return this.formatWithDailyLimit(msg, this.getDailySendLimit(res));
            }
        }
        if (res.error) {
            return res.error;
        }
        return this.t("otp_send_failed");
    }

    async fetchOtpStatus(phone) {
        const actions = this.getOtpActions();
        return this.danhyApi(actions.status, this.buildStatusPayload(phone));
    }

    parseDailySendBlock(status) {
        const data = status?.data || {};
        const phoneBlocked = !!data.phone_blocked;
        const ipBlocked = !!data.ip_blocked;
        const limitType = data.limit_type || "";
        const isDailyBlocked =
            limitType === "daily_phone" ||
            limitType === "daily_ip" ||
            phoneBlocked ||
            ipBlocked;

        if (!isDailyBlocked) {
            return null;
        }

        const error_code = phoneBlocked ? "rate_limit_phone" : "rate_limit_ip";
        return {
            blocked: true,
            error_code,
            message: this.getOtpSendErrorMessage({
                error_code,
                daily_limit_phone: data.daily_limit_phone,
                daily_limit_ip: data.daily_limit_ip,
            }),
            retry_after: Number(data.retry_after ?? 0),
        };
    }

    async checkDailySendBlocked(phone) {
        try {
            const status = await this.fetchOtpStatus(phone);
            const block = this.parseDailySendBlock(status);
            if (block) {
                return { ...block, status };
            }
            return { blocked: false, status };
        } catch (e) {
            console.error("otp_status failed", e);
            return { blocked: false, status: null };
        }
    }

    applyOtpStatusGate(status, phone, now = Date.now()) {
        const data = status?.data || {};
        if (data.call_lock) {
            return {
                action: "call_lock",
                message: this.getCallLockMessage({
                    call_lock: true,
                    call_lock_type: data.call_lock_type,
                }),
                retryAfter: Number(data.retry_after ?? 0),
            };
        }

        if (data.verify_soft_lock) {
            this.applyOtpVerifySoftLock();
        }

        const retryAfter = Number(data.retry_after ?? 0);
        const dailyBlock = this.parseDailySendBlock(status);

        if (dailyBlock) {
            if (dailyBlock.retry_after > 0) {
                this.setResendUntil(phone, now + dailyBlock.retry_after * 1000);
            }
            return { action: "daily_block", dailyBlock, retryAfter };
        }

        if (retryAfter > 0) {
            const until = now + retryAfter * 1000;
            this.setResendUntil(phone, until);
            return { action: "cooldown", until, retryAfter };
        }

        localStorage.removeItem(this.resendStorageKey(phone));
        return { action: "send" };
    }

    getOtpVerifyErrorMessage(res = {}) {
        if (this.isOtpVerifySoftLockResponse(res)) {
            return this.t("otp_verify_soft_blocked");
        }

        const langKey = OTP_VERIFY_ERROR_KEYS[res?.error_code];
        if (langKey) {
            return this.t(langKey);
        }
        if (typeof res?.error === "string" && res.error.trim()) {
            return res.error.trim();
        }
        if (typeof res?.message === "string" && res.message.trim()) {
            return res.message.trim();
        }
        return this.t("otp_verify_failed");
    }

    getCallLockMessage(res = {}) {
        const isDailySend =
            res.call_lock_type === "daily_send" ||
            res.error_code === "otp_send_blocked";
        return this.t(isDailySend ? "otp_verify_call_lock" : "otp_verify_blocked");
    }

    isOtpLockedForToday() {
        return !!this._otpLockedForToday;
    }

    isOtpVerifySoftLocked() {
        return !!this._otpVerifyCodeBurned;
    }

    isOtpCallLockResponse(res = {}) {
        return !!(
            res.call_lock ||
            res.error_code === "otp_send_blocked"
        );
    }

    isOtpVerifySoftLockResponse(res = {}) {
        return !!(
            res.verify_soft_lock ||
            (res.error_code === "otp_verify_blocked" && !res.call_lock)
        );
    }

    applyOtpVerifySoftLock() {
        this._otpVerifyCodeBurned = true;
        this.syncSubmitButtonState();
    }

    clearOtpVerifySoftLock() {
        this._otpVerifyCodeBurned = false;
        this.syncSubmitButtonState();
    }

    applyOtpLockedUi() {
        const $wrapper = $("#otp-verify-modal-wrapper");
        const $modal = $("#otp-verify-modal");
        const $submitBtn = $("#otp-submit-btn");
        const $inputs = $(".otp-input");
        const $overlay = $("#otp-verify-loading-overlay");
        const $resendLink = $(".otp-resend-link");

        $wrapper.addClass("is-otp-locked").removeClass("is-processing");
        $modal.removeClass("is-booking");
        $overlay.addClass("is-hidden").attr("aria-hidden", "true");
        $inputs.prop("disabled", true);
        $submitBtn
            .prop("disabled", true)
            .addClass("disabled")
            .removeClass("is-loading");
        $submitBtn.find(".context").text(this._submitBtnDefaultText);
        $submitBtn.find(".arrow").show();
        $resendLink
            .addClass("is-disabled")
            .removeClass("is-pending")
            .css("pointer-events", "none")
            .attr({ "aria-disabled": "true", tabindex: -1 })
            .text(this.getResendLinkLabel());
    }

    syncSubmitButtonState() {
        const $submitBtn = $("#otp-submit-btn");
        const $inputs = $(".otp-input");
        const isProcessing = $("#otp-verify-modal-wrapper").hasClass("is-processing");
        const allFilled = $inputs.toArray().every((input) => $(input).val().trim() !== "");

        if (this.isOtpLockedForToday() || this.isOtpVerifySoftLocked()) {
            $submitBtn.prop("disabled", true).addClass("disabled");
            return;
        }

        if (isProcessing) {
            $submitBtn.prop("disabled", true).addClass("disabled");
            return;
        }

        $submitBtn.prop("disabled", !allFilled);
        $submitBtn.toggleClass("disabled", !allFilled);
    }

    setActionLoading({ active = false, phase = "" } = {}) {
        const $wrapper = $("#otp-verify-modal-wrapper");
        const $modal = $("#otp-verify-modal");
        const $submitBtn = $("#otp-submit-btn");
        const $inputs = $(".otp-input");
        const $overlay = $("#otp-verify-loading-overlay");
        const $resendLink = $(".otp-resend-link");

        if (!active) {
            $wrapper.removeClass("is-processing");
            $modal.removeClass("is-booking");
            $overlay.addClass("is-hidden").attr("aria-hidden", "true");

            if (this.isOtpLockedForToday()) {
                this.applyOtpLockedUi();
                return;
            }

            $inputs.prop("disabled", false);
            $submitBtn
                .removeClass("disabled")
                .removeClass("is-loading");
            $submitBtn.find(".context").text(this._submitBtnDefaultText);
            $submitBtn.find(".arrow").show();

            if (this.isResendCooldownActive()) {
                $resendLink.addClass("is-disabled").css("pointer-events", "none");
            } else {
                $resendLink
                    .removeClass("is-disabled")
                    .css("pointer-events", "")
                    .attr({ "aria-disabled": "false", tabindex: 0 });
            }
            this.syncSubmitButtonState();
            return;
        }

        $wrapper.addClass("is-processing");
        $modal.toggleClass("is-booking", phase === "booking");
        $overlay.removeClass("is-hidden").attr("aria-hidden", "false");
        $submitBtn.prop("disabled", true).addClass("disabled is-loading");
        $submitBtn.find(".arrow").hide();
        $inputs.prop("disabled", true);
        $resendLink.addClass("is-disabled").css("pointer-events", "none");
        $submitBtn
            .find(".context")
            .text(
                phase === "booking"
                    ? this.getBookingLoadingText()
                    : this.getVerifyLoadingText()
            );
    }

    initCooldown() {
        $(".otp-resend-link").on("click", (e) => {
            e.preventDefault();
            if (this.isOtpLockedForToday()) {
                return;
            }
            this.scheduleResendOTP();
        });
    }

    scheduleResendOTP() {
        if (!this.currentPhone || this.isOtpLockedForToday()) {
            return;
        }

        const phone = this.currentPhone;

        clearTimeout(this._resendDebounceTimer);
        this._resendDebounceTimer = setTimeout(() => {
            this._resendDebounceTimer = null;
            this.sendOTP(phone, { isResend: true });
        }, RESEND_DEBOUNCE_MS);
    }

    focusOtpInput($inputs, index) {
        if (index < 0 || index >= $inputs.length) {
            return;
        }

        const input = $inputs.get(index);
        if (!input) {
            return;
        }

        input.focus({ preventScroll: true });
        if (typeof input.select === "function") {
            input.select();
        }
    }

    setOtpDigit($input, digit, index, $inputs) {
        $input.val(digit);
        this.clearError();

        if (digit !== "") {
            if (index < $inputs.length - 1) {
                this.focusOtpInput($inputs, index + 1);
            } else {
                $input.trigger("blur");
            }
        }

        this.syncSubmitButtonState();
    }

    handleOtpDigitInput($input, index, $inputs) {
        const digits = String($input.val()).replace(/[^0-9]/g, "");
        this.clearError();

        if (!digits) {
            $input.val("");
            this.syncSubmitButtonState();
            return;
        }

        // Typing into a filled cell can append (e.g. "58") — keep only the latest digit.
        // Multi-digit paste is handled separately in the paste handler.
        this.setOtpDigit($input, digits.slice(-1), index, $inputs);
    }

    initInputs() {
        const $inputs = $('.otp-input');
        const $form = $('#otp-form');
        const self = this;

        $inputs.each(function (index) {
        const $input = $(this);
        let isComposing = false;

        $input.on('compositionstart', () => {
            isComposing = true;
        });

        $input.on('compositionend', function () {
            isComposing = false;
            self.handleOtpDigitInput($(this), index, $inputs);
        });

        $input.on('focus', function () {
            if (typeof this.select === "function") {
                this.select();
            }
        });

        // Keyboard events
        $input.on('keydown', function (e) {
            if (e.isComposing || e.key === 'Process') {
                return;
            }

            const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'];
            const isNumber = /^[0-9]$/.test(e.key);
            const isCommand = e.metaKey || e.ctrlKey;

            // Replace current digit instead of appending / pushing to next cell.
            if (isNumber) {
                e.preventDefault();
                self.setOtpDigit($input, e.key, index, $inputs);
                return;
            }

            if (allowedKeys.indexOf(e.key) === -1 && !isCommand) {
                e.preventDefault();
            }

            // Backspace logic
            if (e.key === 'Backspace') {
                e.preventDefault();
                if ($input.val() !== '') {
                    $input.val('');
                } else if (index > 0) {
                    self.focusOtpInput($inputs, index - 1);
                    $inputs.eq(index - 1).val('');
                }
                self.clearError();
                self.syncSubmitButtonState();
            }

            // Delete logic
            if (e.key === 'Delete') {
                e.preventDefault();
                if ($input.val() !== '') {
                    $input.val('');
                }
                self.clearError();
                self.syncSubmitButtonState();
            }

            // Arrow Navigation
            if (e.key === 'ArrowLeft' && index > 0) {
                e.preventDefault();
                self.focusOtpInput($inputs, index - 1);
            }
            if (e.key === 'ArrowRight' && index < $inputs.length - 1) {
                e.preventDefault();
                self.focusOtpInput($inputs, index + 1);
            }
        });

        // Input event (IME / autofill fallback)
        $input.on('input', function () {
            if (isComposing) {
                return;
            }
            self.handleOtpDigitInput($(this), index, $inputs);
        });

        // Handle Paste event
        $input.on('paste', function (e) {
            e.preventDefault();
            const pastedData = (e.originalEvent || e).clipboardData.getData('text').replace(/[^0-9]/g, '');
            self.clearError();

            for (let i = 0; i < pastedData.length; i++) {
                if (index + i < $inputs.length) {
                    $inputs.eq(index + i).val(pastedData[i]);
                }
            }

            const nextIndex = Math.min(index + pastedData.length, $inputs.length - 1);
            self.focusOtpInput($inputs, nextIndex);
            self.syncSubmitButtonState();
        });
        });

        $form.on("submit.otpVerify", (e) => {
            e.preventDefault();
            if ($("#otp-submit-btn").prop("disabled") || this.isOtpLockedForToday() || this.isOtpVerifySoftLocked()) {
                return;
            }
            self.handleVerifySubmit();
        });
    }

    static decodeTokenPayload(token) {
        if (!token || typeof token !== "string") {
            return null;
        }

        const parts = token.split(".");
        if (parts.length !== 3) {
            return null;
        }

        try {
            const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
            const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
            return JSON.parse(atob(padded));
        } catch (error) {
            return null;
        }
    }

    static normalizePhone(phone) {
        let digits = String(phone || "").replace(/\D/g, "");
        if (digits.startsWith("84")) {
            digits = "0" + digits.slice(2);
        }
        return digits;
    }

    static isTokenValidForPhone(token, phone) {
        const payload = OtpVerifyFlow.decodeTokenPayload(token);
        if (!payload) {
            return false;
        }

        const exp = Number(payload.exp || 0);
        if (!exp || exp * 1000 <= Date.now()) {
            return false;
        }

        const tokenPhone = OtpVerifyFlow.normalizePhone(payload.phone);
        const formPhone = OtpVerifyFlow.normalizePhone(phone);
        return tokenPhone !== "" && tokenPhone === formPhone;
    }

    setTokenOnForm($form, token) {
        if (!$form || !$form.length) {
            return;
        }

        let $input = $form.find('[name="otp_verification_token"]');
        if (!$input.length) {
            $form.append(
                '<input type="hidden" name="otp_verification_token" id="otp-verification-token" value="">'
            );
            $input = $form.find('[name="otp_verification_token"]');
        }

        $input.val(token || "");
    }

    clearTokenOnForm($form) {
        if ($form && $form.length) {
            $form.find('[name="otp_verification_token"]').val("");
        }
    }

    closeModal({ clearResendTimer = false } = {}) {
        if (clearResendTimer) {
            this.clearResendState(this.currentPhone);
        }
        this.clearSuccess();
        this.setActionLoading({ active: false });
        $("body").removeClass("show-modal");
        $("#otp-verify-modal-wrapper").removeClass("show-modal is-processing");
        $("#otp-verify-modal").removeClass("active is-booking");
        $("#otp-verify-loading-overlay").addClass("is-hidden");
    }

    async handleVerifySubmit() {
        if (!this.currentPhone) {
            return;
        }

        if (this.isOtpLockedForToday()) {
            this.showError(this.t("otp_verify_blocked"));
            return;
        }

        if (this.isOtpVerifySoftLocked()) {
            this.showError(this.t("otp_verify_soft_blocked"));
            return;
        }

        const $inputs = $(".otp-input");
        const otp = $inputs
            .toArray()
            .map((input) => String($(input).val() || "").trim())
            .join("");

        if (otp.length !== $inputs.length) {
            return;
        }

        let bookingStarted = false;
        this.clearError();
        this.setActionLoading({ active: true, phase: "verify" });

        try {
            const actions = this.getOtpActions();
            const res = await this.danhyApi(
                actions.verify,
                this.buildVerifyPayload(this.currentPhone, otp)
            );

            if (this.isRegisterMode()) {
                if (res?.success) {
                    this.currentPassword = "";
                    this.closeModal({ clearResendTimer: true });
                    if (typeof this.onVerified === "function") {
                        await this.onVerified();
                    }
                    return;
                }

                if (this.isOtpCallLockResponse(res)) {
                    this.lockOtpFormForToday();
                } else if (this.isOtpVerifySoftLockResponse(res)) {
                    this.applyOtpVerifySoftLock();
                }
                this.showError(this.getOtpVerifyErrorMessage(res));
                return;
            }

            const verificationToken =
                res?.otp_verification_token || res?.data?.otp_verification_token || "";

            if (res?.success && verificationToken) {
                if (this.$bookingForm) {
                    this.setTokenOnForm(this.$bookingForm, verificationToken);
                }

                bookingStarted = true;
                this.setActionLoading({ active: true, phase: "booking" });

                try {
                    if (typeof this.onVerified === "function") {
                        await this.onVerified(verificationToken);
                    }
                } catch (error) {
                    console.error("booking after otp failed", error);
                } finally {
                    this.setActionLoading({ active: false });
                }
                return;
            }

            if (this.isOtpCallLockResponse(res)) {
                this.lockOtpFormForToday();
            } else if (this.isOtpVerifySoftLockResponse(res)) {
                this.applyOtpVerifySoftLock();
            }
            this.showError(this.getOtpVerifyErrorMessage(res));
        } catch (error) {
            if (bookingStarted) {
                return;
            }

            console.error("verify_otp failed", error);
            this.showError(
                this.getLang() === "en"
                    ? "Connection error. Please try again."
                    : "Lỗi kết nối. Vui lòng thử lại."
            );
        } finally {
            if (!bookingStarted) {
                this.setActionLoading({ active: false });
            }
        }
    }

    danhyApi(action, payload = {}) {
        const actions = this.getOtpActions();
        if (this.isOtpLockedForToday()) {
            const isSend = action === actions.send || action === "send_otp";
            return Promise.resolve({
                success: false,
                status_code: 429,
                error_code: isSend ? "otp_send_blocked" : "otp_verify_blocked",
                call_lock: true,
                call_lock_type: "verify_fail",
            });
        }

        if (
            (action === actions.verify || action === "verify_otp") &&
            this.isOtpVerifySoftLocked()
        ) {
            return Promise.resolve({
                success: false,
                status_code: 429,
                error_code: "otp_verify_blocked",
                call_lock: false,
                verify_soft_lock: true,
                retry_after: 0,
            });
        }

        const root = typeof HM_Variables !== "undefined" ? HM_Variables.root_url || "" : "";
        const nonce = typeof HM_Variables !== "undefined" ? HM_Variables.rest_nonce || "" : "";
        return fetch(`${root}/wp-json/hmc/v1/danhy`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-WP-Nonce": nonce,
            },
            body: JSON.stringify({ action, payload }),
        }).then((r) => r.json());
    }

    resendStorageKey(phone) {
        const identity = String(phone || "").trim().toLowerCase();
        const prefix = this.isRegisterMode() ? "hm_reg_otp_resend_until_" : "hm_otp_resend_until_";
        if (identity.includes("@")) {
            return `${prefix}${identity}`;
        }
        const digits = identity.replace(/\D/g, "");
        return `${prefix}${digits}`;
    }

    getResendUntil(phone) {
        return Number(localStorage.getItem(this.resendStorageKey(phone)) || 0);
    }
    
    setResendUntil(phone, untilMs) {
        localStorage.setItem(this.resendStorageKey(phone), String(untilMs));
    }

    clearResendState(phone) {
        const targetPhone = phone || this.currentPhone;
        if (!targetPhone) {
            return;
        }

        clearInterval(this._resendTimer);
        this._resendTimer = null;
        localStorage.removeItem(this.resendStorageKey(targetPhone));

        const $link = $(".otp-resend-link");
        $link
            .removeClass("is-disabled is-pending")
            .css("pointer-events", "")
            .text(this.getResendLinkLabel());
    }

    getResendLinkLabel() {
        const $link = $(".otp-resend-link");
        if (!$link.data("default-label")) {
          $link.data("default-label", $link.text().trim());
        }
        return $link.data("default-label");
    }

    isResendCooldownActive(phone = this.currentPhone) {
        if (!phone) return false;
        return Date.now() < this.getResendUntil(phone);
    }

    setResendLinkPending(pending) {
        const $link = $(".otp-resend-link");
        if (this.isOtpLockedForToday()) {
            this.setResendLinkBlockedForToday();
            return;
        }

        if (pending) {
            $link.addClass("is-pending is-disabled").css("pointer-events", "none");
            return;
        }

        $link.removeClass("is-pending");

        if (this.isResendCooldownActive()) {
            $link.addClass("is-disabled").css("pointer-events", "none");
            return;
        }

        $link.removeClass("is-disabled").css("pointer-events", "");
        $link.text(this.getResendLinkLabel());
    }

    setResendLinkBlockedForToday() {
        const $link = $(".otp-resend-link");
        clearInterval(this._resendTimer);
        this._resendTimer = null;
        $link
            .addClass("is-disabled")
            .removeClass("is-pending")
            .css("pointer-events", "none")
            .attr({ "aria-disabled": "true", tabindex: -1 })
            .text(this.getResendLinkLabel());
    }

    lockOtpFormForToday() {
        this._otpLockedForToday = true;
        if (this.currentPhone) {
            this.clearResendState(this.currentPhone);
        }
        this.applyOtpLockedUi();
    }

   startResendCountdown(phone, untilMs) {
        if (this.isOtpLockedForToday()) {
            this.applyOtpLockedUi();
            return;
        }

        const $link = $(".otp-resend-link");
        const defaultLabel = this.getResendLinkLabel();
        clearInterval(this._resendTimer);
        const tick = () => {
            if (this._sendOtpInFlight) {
                return;
            }

            const remainingSec = Math.ceil(Math.max(0, untilMs - Date.now()) / 1000);
            if (remainingSec > 0) {
                $link.addClass("is-disabled").css("pointer-events", "none");
                $link.removeClass("is-pending");
                $link.text(`${defaultLabel} (${remainingSec}s)`);
            } else {
                clearInterval(this._resendTimer);
                $link.removeClass("is-disabled is-pending").css("pointer-events", "");
                $link.text(defaultLabel);
                localStorage.removeItem(this.resendStorageKey(phone));
            }
        };
        tick();
        this._resendTimer = setInterval(tick, 1000);
    }

    sendOTP(phone, { isResend = false, cachedStatus = null } = {}) {
        if (this.isOtpLockedForToday()) {
            return Promise.resolve();
        }

        if (this._sendOtpInFlight) {
            return this._sendOtpInFlight;
        }

        this.setResendLinkPending(true);
        this._sendOtpInFlight = this.runSendOTP(phone, { isResend, cachedStatus }).finally(() => {
            this._sendOtpInFlight = null;
            this.setResendLinkPending(false);
        });

        return this._sendOtpInFlight;
    }

    async runSendOTP(phone, { isResend = false, cachedStatus = null } = {}) {
        const now = Date.now();
        const localUntil = this.getResendUntil(phone);

        try {
            const status = cachedStatus || (await this.fetchOtpStatus(phone));
            const gate = this.applyOtpStatusGate(status, phone, now);

            if (gate.action === "call_lock") {
                this.lockOtpFormForToday();
                this.showError(gate.message);
                return;
            }

            if (gate.action === "daily_block") {
                this.setResendLinkBlockedForToday();
                this.showError(gate.dailyBlock.message);
                return;
            }

            if (gate.action === "cooldown") {
                this.startResendCountdown(phone, gate.until);
                if (this.isOtpVerifySoftLocked()) {
                    this.showError(this.t("otp_verify_soft_blocked"));
                }
                return;
            }
        } catch (e) {
            console.error("otp_status failed", e);
            if (localUntil > now) {
                this.startResendCountdown(phone, localUntil);
                return;
            }
        }

        // cooldown expired -> send OTP
        try {
            const actions = this.getOtpActions();
            const res = await this.danhyApi(actions.send, this.buildSendPayload(phone));
            if (res.success) {
                this.clearOtpVerifySoftLock();
                this.resetOtpInputs();
                this.clearError();
                const cooldownSec = res.retry_after ?? 120;
                const until = Date.now() + cooldownSec * 1000;
                this.setResendUntil(phone, until);
                this.startResendCountdown(phone, until);
                if (isResend) {
                    this.showSuccess(this.t("otp_verify_modal_resend_success"));
                }
                return;
            }

            // Day lock (call lock) -> lock form + resend, no countdown, i18n message
            if (this.isOtpCallLockResponse(res)) {
                this.lockOtpFormForToday();
                this.setResendLinkBlockedForToday();
                this.showError(this.getCallLockMessage(res));
                return;
            }

            if (res.error_code === "rate_limit_phone" || res.error_code === "rate_limit_ip") {
                if (res.retry_after > 0) {
                    const until = Date.now() + res.retry_after * 1000;
                    this.setResendUntil(phone, until);
                }
                this.setResendLinkBlockedForToday();
                this.showError(this.getOtpSendErrorMessage(res));
                return;
            }

            // 429 or other -> sync countdown from server
            if (res.retry_after > 0) {
            const until = Date.now() + res.retry_after * 1000;
            this.setResendUntil(phone, until);
            this.startResendCountdown(phone, until);
            }
            this.showError(this.getOtpSendErrorMessage(res));
        } catch (e) {
            console.error("send_otp failed", e);
            this.showError("Connection error. Please try again.");
        }
    }

    showError(msg) {
        this.clearSuccess();
        const $box = $("#otp-message-box");
        const $inputs = $(".otp-input");
        if ($box.length) {
            $inputs.addClass("err");
            $box.text(msg).show();
        } else {
            console.warn(msg);
        }
    }

    showSuccess(msg) {
        this.clearSuccess();
        const $box = $("#otp-resend-success-box");
        if (!$box.length) {
            return;
        }

        $("#otp-message-box").text("").hide();
        $(".otp-input").removeClass("err");
        $box.text(msg).show();
        this._successMessageTimer = setTimeout(() => {
            this._successMessageTimer = null;
            $box.text("").hide();
        }, RESEND_SUCCESS_TIMEOUT_MS);
    }

    clearSuccess() {
        clearTimeout(this._successMessageTimer);
        this._successMessageTimer = null;
        $("#otp-resend-success-box").text("").hide();
    }

    clearError() {
        this.clearSuccess();
        $("#otp-message-box").text("").hide();
        $(".otp-input").removeClass("err");
    }
      
    resetOtpInputs() {
        const $inputs = $(".otp-input");
        $inputs.val("");
        this.syncSubmitButtonState();
    }

    focusFirstOtpInput() {
        const focus = () => {
            if (!$("#otp-verify-modal-wrapper").hasClass("show-modal")) {
                return;
            }

            const input = $(".otp-input").not(":disabled").get(0);
            if (!input) {
                return;
            }

            input.focus({ preventScroll: true });
            if (typeof input.setSelectionRange === "function") {
                const len = input.value.length;
                input.setSelectionRange(len, len);
            }
        };

        requestAnimationFrame(() => {
            requestAnimationFrame(focus);
        });
    }

    open({
        phone,
        identifier,
        mode = "booking",
        password = "",
        skipInitialSend = false,
        retryAfter = null,
        $bookingForm,
        onVerified,
        cachedOtpStatus = null,
    } = {}) {
        this.currentMode = mode === "register" ? "register" : "booking";
        this.currentPhone = identifier || phone || "";
        this.currentPassword = this.isRegisterMode() ? String(password || "") : "";
        this.$bookingForm = $bookingForm;
        this.onVerified = onVerified;
        this._otpLockedForToday = false;
        this.clearOtpVerifySoftLock();

        clearTimeout(this._resendDebounceTimer);
        this._resendDebounceTimer = null;

        this.clearError();
        this.resetOtpInputs();
        $("#otp-verify-modal-wrapper").removeClass("is-otp-locked");

        clearInterval(this._resendTimer);
        this._resendTimer = null;
        $(".otp-resend-link")
            .removeClass("is-disabled is-pending")
            .css("pointer-events", "")
            .attr({ "aria-disabled": "false", tabindex: 0 })
            .text(this.getResendLinkLabel());
        this.setActionLoading({ active: false });

        // Inject contact + channel copy (phone vs email)
        const isEmail = String(this.currentPhone || "").includes("@");
        const $title = $(".otp-verify-modal-title");
        if ($title.length) {
            const titleText = isEmail
                ? $title.data("title-email") || $title.data("titleEmail")
                : $title.data("title-phone") || $title.data("titlePhone");
            if (titleText) {
                $title.text(titleText);
            }
        }
        const $desc = $(".otp-verify-modal-desc-text");
        if ($desc.length) {
            const descText = isEmail
                ? $desc.data("desc-email") || $desc.data("descEmail")
                : $desc.data("desc-phone") || $desc.data("descPhone");
            if (descText) {
                $desc.text(descText);
            }
        }
        $(".otp-verify-modal-phone").text(this.currentPhone);

        // open modal
        $("body").addClass("show-modal");
        $("#otp-verify-modal-wrapper").addClass("show-modal");
        $("#otp-verify-modal").addClass("active");

        this.focusFirstOtpInput();
        this.syncSubmitButtonState();

        if (skipInitialSend) {
            const cooldownSec = Number(retryAfter);
            if (cooldownSec > 0) {
                const until = Date.now() + cooldownSec * 1000;
                this.setResendUntil(this.currentPhone, until);
                this.startResendCountdown(this.currentPhone, until);
            }
            return;
        }

        // check cooldown and send OTP
        this.sendOTP(this.currentPhone, { cachedStatus: cachedOtpStatus });
    }
  }